// Small helpers for deriving extra shades from a single org-chosen brand
// colour, so clubs only have to pick one colour instead of several
// (a hover-darkened shade, plus a few translucent tints for subtle
// backgrounds/borders that used to be hardcoded Tailwind classes).

function hexToRgb(hex) {
  const clean = (hex || "").replace("#", "");
  const full = clean.length === 3
    ? clean.split("").map((c) => c + c).join("")
    : clean;
  const num = parseInt(full, 16);
  if (Number.isNaN(num)) return { r: 48, g: 48, b: 48 }; // falls back to the default brand grey
  return { r: (num >> 16) & 255, g: (num >> 8) & 255, b: num & 255 };
}

// Darkens (negative percent) or lightens (positive percent) a hex colour.
// Used to derive a hover state from the base brand colour.
export function shadeColor(hex, percent) {
  const { r, g, b } = hexToRgb(hex);
  const amount = Math.round((percent / 100) * 255);
  const clamp = (v) => Math.max(0, Math.min(255, v + amount));
  const toHex = (v) => v.toString(16).padStart(2, "0");
  return `#${toHex(clamp(r))}${toHex(clamp(g))}${toHex(clamp(b))}`.toUpperCase();
}

// Translucent version of a hex colour, for subtle tinted backgrounds/borders.
export function hexToRgba(hex, alpha) {
  const { r, g, b } = hexToRgb(hex);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

export const DEFAULT_BRAND_COLOR = "#303030";

// Applies an org's brand colour (and its derived shades) as CSS custom
// properties on the document root, so Tailwind arbitrary-value classes like
// `bg-[var(--brand)]` pick it up anywhere in the authenticated app.
export function applyBrandColor(hex) {
  const brand = hex || DEFAULT_BRAND_COLOR;
  const root = document.documentElement.style;
  root.setProperty("--brand", brand);
  root.setProperty("--brand-dark", shadeColor(brand, -35));
  root.setProperty("--brand-tint-5", hexToRgba(brand, 0.05));
  root.setProperty("--brand-tint-20", hexToRgba(brand, 0.2));
  root.setProperty("--brand-tint-50", hexToRgba(brand, 0.5));
}
